import { round } from "lodash";
import { HeightUnit, WeightUnit } from "types";

const KG_TO_LB = 2.2046;
const LB_TO_KG = 1 / KG_TO_LB;

const INCH_TO_CM = 2.54;
const CM_TO_INCH = 1 / INCH_TO_CM;

export const convertWeight = (value: number, from: WeightUnit, to: WeightUnit): number => {
    if (from === to) return value;

    if (from === "kg" && to === "lbs") {
        return value * KG_TO_LB;
    }
    if (from === "lbs" && to === "kg") {
        return value * LB_TO_KG;
    }

    return value;
};

// When converting to FEET, the string is formatted as "feet.inches" (e.g., "5.06" for 5 feet 6 inches)
// value can be a number (inches or cm), a string in "feet.inches" format, or a natural "5' 6\"" format
export const convertHeight = (value: string | number, from: HeightUnit, to: HeightUnit): string => {
    let valueStr = String(value).trim();
    if (!valueStr) return "";

    // Normalize natural feet/inches formats from waivers into total inches.
    // Handles: "5' 6\"", "5'6\"", "6'4", "5'4"" (curly/smart quotes)
    if (from === HeightUnit.FEET) {
        const feetInchesMatch = valueStr.match(/^(\d+)['\u2018\u2019]\s*(\d+)?["\u201C\u201D]?\s*$/);
        if (feetInchesMatch) {
            const feet = parseInt(feetInchesMatch[1], 10);
            const inches = parseInt(feetInchesMatch[2] ?? "0", 10);
            valueStr = String(feet * 12 + inches);
        }
    }

    if (from === HeightUnit.FEET && to === HeightUnit.FEET) {
        if (/[.,]/.test(valueStr)) {
            return valueStr;
        }
    }

    if (from === HeightUnit.CM && to === HeightUnit.CM) {
        return valueStr;
    }

    let inches: number;

    if (from === HeightUnit.CM) {
        inches = Number(valueStr) * CM_TO_INCH;
    } else {
        inches = Number(valueStr);
    }

    if (!Number.isFinite(inches)) {
        return "";
    }

    if (to === HeightUnit.CM) {
        const cm = inches * INCH_TO_CM;
        return round(cm).toString();
    }

    let totalInches = inches;
    let feet = Math.floor(totalInches / 12);
    let inch = Math.round(totalInches % 12);

    if (inch === 12) {
        inch = 0;
        feet += 1;
    }
    if (inch === 0) return feet.toFixed(2);
    return `${feet}.${inch.toString().padStart(2, "0")}`;
};
